import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  AlertTriangle, 
  AlertCircle, 
  Shield, 
  RefreshCw,
  X,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProtectionAlerts({ onReloadClick }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);

  useEffect(() => {
    loadAlerts();
    // Refresh alerts every 30 seconds
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/protection-alerts`, {
        withCredentials: true
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Load alerts error:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = (clubId) => {
    setDismissedAlerts(prev => [...prev, clubId]);
  };

  const visibleAlerts = alerts.filter(a => !dismissedAlerts.includes(a.club_id));

  if (loading || visibleAlerts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6">
      <AnimatePresence>
        {visibleAlerts.map((alert, index) => (
          <motion.div
            key={alert.club_id}
            initial={{ opacity: 0, y: -20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -20, height: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Alert
              className={`relative border-l-4 ${
                alert.severity === 'critical' 
                  ? 'border-l-red-500 bg-red-50' 
                  : 'border-l-amber-500 bg-amber-50'
              }`}
              data-testid={`protection-alert-${alert.club_id}`}
            >
              {/* Dismiss button */}
              <button
                onClick={() => dismissAlert(alert.club_id)}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/50 transition-colors"
                aria-label="Dismiss alert"
              >
                <X className="w-4 h-4 text-stone-400" />
              </button>

              <div className="flex items-start">
                {/* Icon */}
                <div className={`p-2 rounded-full mr-3 ${
                  alert.severity === 'critical' ? 'bg-red-100' : 'bg-amber-100'
                }`}>
                  {alert.severity === 'critical' ? (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pr-8">
                  <AlertTitle className={`font-semibold mb-1 ${
                    alert.severity === 'critical' ? 'text-red-800' : 'text-amber-800'
                  }`}>
                    {alert.severity === 'critical' ? 'Critical: ' : 'Warning: '}
                    {alert.club_name}
                  </AlertTitle>
                  
                  <AlertDescription className={`text-sm ${
                    alert.severity === 'critical' ? 'text-red-700' : 'text-amber-700'
                  }`}>
                    {alert.message}
                  </AlertDescription>

                  {/* Progress Bar */}
                  <div className="mt-3 mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-stone-600">
                        ${alert.current.toFixed(2)} / ${alert.required.toFixed(2)}
                      </span>
                      <span className={`font-medium ${
                        alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'
                      }`}>
                        {alert.percentage}%
                      </span>
                    </div>
                    <Progress 
                      value={alert.percentage} 
                      className={`h-2 ${
                        alert.severity === 'critical' ? 'bg-red-200' : 'bg-amber-200'
                      }`}
                    />
                  </div>

                  {/* Action Button */}
                  <Button
                    size="sm"
                    onClick={() => onReloadClick?.(alert.club_id)}
                    className={`mt-2 ${
                      alert.severity === 'critical' 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-amber-600 hover:bg-amber-700'
                    }`}
                    data-testid={`reload-protection-${alert.club_id}`}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reload Protection
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>

                  {alert.action_blocked && (
                    <p className="text-xs text-red-600 mt-2 flex items-center">
                      <Shield className="w-3 h-3 mr-1" />
                      Payouts and withdrawals are blocked until reloaded
                    </p>
                  )}
                </div>
              </div>
            </Alert>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
